import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'

export const SPEED_ORDER_UPLOAD_API = Symbol('SPEED_ORDER_UPLOAD_API')

export interface CorrectionItem {
  row: number
  product_number: string
  name: string
  original_quantity: number
  suggested_quantity: number
}

export interface ErrorItem {
  message: string
  name: string
  product_number: string
  quantity: number
  row: number
}

export interface ValidateResult {
  can_import: boolean
  corrections: CorrectionItem[]
  errors: ErrorItem[]
  total_products: number
}

export interface UploadResult {
  success: boolean
  message?: string
  rows?: SpeedOrderRow[]
  errors?: string[]
  file_id?: string
  csv_id?: string
  csv_settings?: any
  header_cols?: string[]
  corrections?: CorrectionItem[]
  can_import?: boolean
}

export interface SpeedOrderRow {
  filled?: boolean
  productName?: string
  productCode?: string
  price?: number
  quantity?: string | number
  productId?: number
  isSearching?: boolean
  hasError?: boolean
  defaultUom?: string
  orderMinimumQty?: number
  orderDefaultQty?: number
  currencySymbol?: string
}

export interface ImportResult {
  success: boolean
  message?: string
  imported_count?: number
  errors?: ErrorItem[]
  rows?: SpeedOrderRow[]
  records?: any[]
  count_errors?: number
  count_ok?: number
}

export class SpeedOrderUploadRequest {
  endpointUrl: URL
  method = 'POST' as const
  controller: AbortController = new AbortController()

  constructor(uploadEndpoint: string, public file: File) {
    this.endpointUrl = new URL(uploadEndpoint, window.location.origin)
  }

  get formData(): FormData {
    const formData = new FormData()
    formData.append('file', this.file)
    return formData
  }
}

export class SpeedOrderValidateRequest {
  endpointUrl: URL
  method = 'POST' as const
  controller: AbortController = new AbortController()

  constructor(
    validateEndpoint: string, 
    public fileId: string, 
    public csvSettings: any,
    public hasHeaders: boolean = true,
    public codeColumn: number = 0,
    public quantityColumn: number = 1
  ) {
    this.endpointUrl = new URL(validateEndpoint, window.location.origin)
  }

  get formData(): FormData {
    const formData = new FormData()
    formData.set('csv_id', this.fileId)
    formData.set('csv_settings', JSON.stringify(this.csvSettings))
    formData.set('col_headers', this.hasHeaders.toString())
    formData.set('col_code', this.codeColumn.toString())
    formData.set('col_qty', this.quantityColumn.toString())
    return formData
  }
}

export class SpeedOrderImportRequest {
  endpointUrl: URL
  method = 'POST' as const
  controller: AbortController = new AbortController()

  constructor(
    importEndpoint: string, 
    public fileId: string, 
    public csvSettings: any, 
    public errors?: any[],
    public corrections?: CorrectionItem[],
    public hasHeaders: boolean = true,
    public codeColumn: number = 0,
    public quantityColumn: number = 1
  ) {
    // Ensure relative URL stays relative and gets proxied correctly
    this.endpointUrl = new URL(importEndpoint, window.location.origin)
  }

  get formData(): FormData {
    const formData = new FormData()
    formData.set('csv_id', this.fileId)
    formData.set('csv_settings', JSON.stringify(this.csvSettings))
    formData.set('col_headers', this.hasHeaders.toString())
    formData.set('col_code', this.codeColumn.toString())
    formData.set('col_qty', this.quantityColumn.toString())
    if (this.errors) {
      formData.set('errors', JSON.stringify(this.errors))
    }
    if (this.corrections) {
      formData.set('corrections', JSON.stringify(this.corrections))
    }
    return formData
  }
}

export interface SpeedOrderUploadAPI {
  upload(request: SpeedOrderUploadRequest): Promise<UploadResult>
  validate(request: SpeedOrderValidateRequest): Promise<ValidateResult>
  import(request: SpeedOrderImportRequest): Promise<ImportResult>
}

@registerDependency(REAL, SPEED_ORDER_UPLOAD_API)
export class SpeedOrderUploadService implements SpeedOrderUploadAPI {
  requests: (SpeedOrderUploadRequest | SpeedOrderValidateRequest | SpeedOrderImportRequest)[] = []

  async upload(request: SpeedOrderUploadRequest): Promise<UploadResult> {
    this.requests.push(request)

    try {
      const response = await fetch(request.endpointUrl, {
        method: request.method,
        body: request.formData,
        signal: request.controller.signal,
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      return result as UploadResult
    } catch (error) {
      console.error('File upload failed:', error)
      throw error
    }
  }

  async validate(request: SpeedOrderValidateRequest): Promise<ValidateResult> {
    this.requests.push(request)
    
    try {
      // Use 'include' to send cookies to all domains (proxy + backend)
      const response = await fetch(request.endpointUrl, {
        method: request.method,
        body: request.formData,
        signal: request.controller.signal,
        credentials: 'include'
      })
      if (!response.ok) {
        throw new Error(`Validation failed: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      return result as ValidateResult
    } catch (error) {
      console.error('File validation failed:', error)
      throw error
    }
  }

  async import(request: SpeedOrderImportRequest): Promise<ImportResult> {
    this.requests.push(request)

    try {
      // Use 'include' to send cookies to all domains (proxy + backend)
      const response = await fetch(request.endpointUrl, {
        method: request.method,
        body: request.formData,
        signal: request.controller.signal,
        credentials: 'include'
      })
      if (!response.ok) {
        throw new Error(`Import failed: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      return result as ImportResult
    } catch (error) {
      console.error('File import failed:', error)
      throw error
    }
  }
}

@registerDependency(FAKE, SPEED_ORDER_UPLOAD_API)
export class FakeSpeedOrderUploadService implements SpeedOrderUploadAPI {
  importRequests: SpeedOrderImportRequest[] = []
  uploadResult: UploadResult = { success: true, csv_id: 'fake-csv-id', csv_settings: {} }
  validateResult: ValidateResult = { can_import: true, corrections: [], errors: [], total_products: 0 }
  importResult: ImportResult = { success: true, records: [], errors: [] }

  async upload(): Promise<UploadResult> {
    return this.uploadResult
  }

  async validate(): Promise<ValidateResult> {
    return this.validateResult
  }

  async import(request: SpeedOrderImportRequest): Promise<ImportResult> {
    this.importRequests.push(request)
    return this.importResult
  }
}

